import { Fragment } from "react"
import { Home } from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"

type Crumb = {
  label: string
  to?: string
}

// app-shell.md's per-route breadcrumb trail, scoped to the routes that
// actually exist in this package so far (`AppRoutes.tsx`) — the wireframe's
// full route set (schedule, warehouse, clients, …) has no page here yet, so
// no crumb claims to describe one.
const pageCrumbs: Record<string, Crumb[]> = {
  "/documents": [{ label: "Документи ОТК" }],
  "/documents/search": [
    { label: "Документи ОТК", to: "/documents" },
    { label: "Розширений Пошук" },
  ],
  "/subject/profile": [
    { label: "Суб'єкт ОТК", to: "/subject" },
    { label: "Профіль та Пункти ОТК" },
  ],
  "/subject/responsible": [
    { label: "Суб'єкт ОТК", to: "/subject" },
    { label: "Відповідальні особи" },
  ],
  "/subject/users": [
    { label: "Суб'єкт ОТК", to: "/subject" },
    { label: "Користувачі" },
  ],
  "/subject/roles": [
    { label: "Суб'єкт ОТК", to: "/subject" },
    { label: "Ролі в АРМ" },
  ],
  "/print-profiles": [{ label: "Профілі друку та калібрування" }],
}

// `/documents/:id` and `/documents/edit/:id` are overlays on the list
// rather than separate pages, so the trail keeps only "Документи ОТК" —
// same rule `/documents/search/:id` follows for its own parent crumb.
function documentCrumbs(pathname: string): Crumb[] | undefined {
  if (
    pathname === "/documents/search" ||
    pathname.startsWith("/documents/search/")
  ) {
    return [
      { label: "Документи ОТК", to: "/documents" },
      { label: "Розширений Пошук" },
    ]
  }

  if (pathname.startsWith("/documents/")) {
    return [{ label: "Документи ОТК" }]
  }

  return undefined
}

export function AppBreadcrumbs() {
  const { pathname } = useLocation()
  const crumbs = pageCrumbs[pathname] ?? documentCrumbs(pathname) ?? []

  return (
    <Breadcrumb className="min-w-0 flex-1 overflow-hidden">
      <BreadcrumbList className="flex-nowrap overflow-hidden">
        <BreadcrumbItem className="shrink-0">
          <BreadcrumbLink asChild>
            <NavLink className="inline-flex items-center gap-1.5" to="/">
              <Home aria-hidden="true" className="size-4" />
              Робочий простір
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => {
          const isCurrent = index === crumbs.length - 1
          return (
            <Fragment key={`${crumb.label}-${index}`}>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbItem className={isCurrent ? "min-w-0" : "shrink-0"}>
                {crumb.to && !isCurrent ? (
                  <BreadcrumbLink asChild>
                    <NavLink to={crumb.to}>{crumb.label}</NavLink>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage className="truncate">
                    {crumb.label}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
